// ABOUTME: API client for self-service data source operations.
// ABOUTME: Handles testing connections and creating data sources for the wizard flow.

import { apiFetch } from './client';
import type { CreateDemoDataSourceRequest, DataSource, DemoDataSourceResponse } from '@/types/api';

/**
 * Credentials for connecting to a Databricks workspace.
 */
export interface ConnectionCredentials {
  workspaceUrl: string;
  httpPath: string;
  accessToken: string;
}

/**
 * Result from testing a data source connection.
 * On success, includes discovered catalogs and schemas.
 * On failure, includes the error message.
 */
export interface TestConnectionResult {
  success: boolean;
  catalogs?: string[];
  schemas?: string[];
  error?: string;
}

/**
 * Configuration for creating a new data source.
 */
export interface DataSourceConfig {
  name: string;
  type: 'databricks';
  config: {
    host: string;
    http_path: string;
    catalog: string;
    schema: string;
  };
  credentials: {
    access_token: string;
  };
}

/**
 * API client for data source wizard operations.
 * These endpoints support the self-service data source connection flow.
 */
export const dataSourceApi = {
  /**
   * Test a connection to a Databricks workspace.
   * Validates credentials and discovers available catalogs/schemas.
   */
  async testConnection(credentials: ConnectionCredentials): Promise<TestConnectionResult> {
    return apiFetch<TestConnectionResult>('/data-sources/test-connection', {
      method: 'POST',
      body: JSON.stringify({
        type: 'databricks',
        config: {
          host: credentials.workspaceUrl,
          http_path: credentials.httpPath,
        },
        credentials: {
          access_token: credentials.accessToken,
        },
      }),
    });
  },

  /**
   * Create a new data source with the provided configuration.
   */
  async createDataSource(config: DataSourceConfig): Promise<DataSource> {
    return apiFetch<DataSource>('/data-sources', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  /**
   * Create a demo data source using a pre-configured sample dataset.
   */
  async createDemo(data: CreateDemoDataSourceRequest): Promise<DemoDataSourceResponse> {
    return apiFetch<DemoDataSourceResponse>('/data-sources/demo', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Dismiss the demo data banner for the current workspace.
   */
  async dismissDemo(): Promise<{ dismissed: boolean }> {
    return apiFetch<{ dismissed: boolean }>('/data-sources/demo/dismiss', {
      method: 'PATCH',
    });
  },

  /**
   * Register user interest in an unsupported data source type.
   * Used to track demand for connectors not yet built.
   */
  async registerInterest(data: { source_type: string }): Promise<{ id: string; source_type: string; created_at: string }> {
    return apiFetch<{ id: string; source_type: string; created_at: string }>('/data-sources/interest', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
